import { Account } from '@/client/accounts/types/account.type';
import {
  OsintTransparencyResponse,
  OsintTransparencyState,
} from '@/client/accounts/types/osint-transparency.type';
import { Subjects } from '@/client/users';
import { AppRoutesPaths } from '@/common/constants';
import { useOsintTransparency } from '@/hooks/query/osint-transparency.hooks';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { AppButton } from '@/ui/buttons';
import AppChip from '@/ui/chip/AppChip';
import { AppIconTooltip } from '@/ui/icon-tooltip/AppIconTooltip';
import { FishingHookIcon } from '@/utils/helpers/icons.helper';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

/* ── Card shell ── */

const StyledCard = styled.div`
  border: 1px solid var(--gray-moderate);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding-small);
  width: 100%;
  background: var(--white-main);
`;

/* ── Header row ── */

const StyledHeader = styled.button`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-align: left;
`;

const StyledTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--semi-xsmall-padding);

  .p-chip {
    font-size: var(--xsmall-font-size);
    line-height: 1.5;
    font-weight: 400;
  }
`;

const StyledTitle = styled.span`
  font-size: var(--medium-font-size);
  font-weight: 600;
  line-height: 1.5;
`;

const StyledChevron = styled.i<{ $isOpen: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
  font-size: 12px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-darker);
  flex-shrink: 0;
`;

/* ── Description ── */

const StyledDescription = styled.p`
  font-size: var(--xsmall-font-size);
  font-weight: 400;
  line-height: 1.5;
  color: var(--black-main);
  margin: var(--semi-xsmall-padding) 0 0 0;
`;

const StyledHelperText = styled.p`
  font-size: var(--xsmall-font-size);
  font-weight: 400;
  line-height: 1.5;
  color: var(--gray-darker);
  margin: var(--xxsmall-padding) 0 0 0;
`;

/* ── Content area ── */

const StyledContent = styled.div`
  margin-top: var(--default-padding-small);
  display: flex;
  flex-direction: column;
  gap: var(--default-padding);
`;

/* ── Report panel (beige) ── */

const StyledReportPanel = styled.div`
  background: var(--beige-main);
  border-radius: var(--small-border-radius);
  padding: 16px;
`;

const StyledSectionTitle = styled.h4`
  font-size: var(--small-font-size);
  font-weight: 700;
  line-height: 1.5;
  margin: 0 0 var(--xsmall-padding) 0;
`;

const StyledSectionText = styled.p`
  font-size: var(--small-font-size);
  font-weight: 500;
  line-height: 1.5;
  margin: 0;
`;

const StyledTimestamp = styled.p`
  font-size: var(--xsmall-font-size);
  font-weight: 400;
  color: var(--gray-darker);
  margin: var(--default-padding-small) 0 0 0;
  line-height: 1.5;
`;

const StyledSectionDivider = styled.div`
  margin: var(--default-padding-small) 0;
`;

const StyledItemList = styled.ol`
  margin: var(--xsmall-padding) 0 0 0;
  padding-left: var(--default-padding-small);
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
  font-size: var(--small-font-size);
  font-weight: 500;
  line-height: 1.5;
`;

const StyledItemLabel = styled.span`
  font-weight: 700;
`;

/* ── Disabled panel (beige with centered CTA) ── */

const StyledDisabledPanel = styled.div`
  background: var(--beige-main);
  border-radius: var(--small-border-radius);
  padding: var(--big-padding) var(--big-padding) 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--xsmall-padding);
`;

const StyledMagnifyingGlass = styled.div`
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 48px;
    color: var(--gray-darker);
  }
`;

const StyledDisabledHeading = styled.h3`
  font-size: var(--heading-small-font-size);
  font-weight: 700;
  line-height: 1.5;
  margin: 0;
`;

const StyledDisabledDescription = styled.p`
  font-size: var(--small-font-size);
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
`;

const StyledDisabledButton = styled(AppButton)`
  margin-top: calc(var(--medium-big-padding) - var(--xsmall-padding));
`;

/* ── CTA banner (dark teal card) ── */

const StyledBanner = styled.div`
  background: var(--teal-deep);
  border-radius: var(--small-border-radius);
  padding: var(--medium-big-padding);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--medium-padding);
`;

const StyledBannerIconBadge = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(14, 151, 151, 0.2);
  border-radius: var(--box-border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--xxsmall-padding);

  i {
    font-size: 24px;
    color: var(--white-main);
  }
`;

const StyledBannerHeading = styled.h4`
  font-size: var(--heading-small-font-size);
  font-weight: 700;
  line-height: 1.5;
  margin: 0;
  color: var(--white-main);
  text-align: center;
`;

const StyledBannerTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--semi-xsmall-padding);
`;

const StyledBannerText = styled.p`
  font-size: var(--small-font-size);
  font-weight: 500;
  line-height: 1.5;
  margin: 0;
  color: var(--white-main);
  text-align: center;
`;

/* ── Types ── */

type Props = {
  accountId: string;
  account?: Account;
};

const getChipProps = (state: OsintTransparencyState) => {
  switch (state) {
    case 'success':
    case 'free-trial-upsell':
    case 'paying-upsell':
    case 'try-planner':
      return { labelKey: 'generic.active', type: 'primary' as const };
    case 'pending':
      return {
        labelKey: 'account.osintTransparency.chip.scraping',
        type: 'yellow' as const,
      };
    case 'disabled':
      return { labelKey: 'generic.disabled', type: 'error' as const };
  }
};

/* ── Main component ── */

export const OsintTransparencySection: React.FC<Props> = ({
  accountId,
  account,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountCanUseFeature } = useFeatureFlag();

  const canViewOsint = accountCanUseFeature(
    Subjects.OSINT_TRANSPARENCY,
    account?.featureFlagAbility ?? [],
  );

  const { data, isLoading } = useOsintTransparency(accountId, canViewOsint);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (data?.state === 'disabled') {
      setIsOpen(true);
    }
  }, [data?.state]);

  if (!canViewOsint || isLoading || !data) {
    return null;
  }

  const chipProps = getChipProps(data.state);
  const isPending = data.state === 'pending';

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleTurnOn = () => {
    navigate(
      `${AppRoutesPaths.ACCOUNT_DATA_CONTROL_ROUTE.replace(
        ':id',
        accountId,
      )}#data-enrichment`,
    );
  };

  const handleBookDemo = () => {
    navigate(AppRoutesPaths.HUBSPOT_BOOKING_ROUTE);
  };

  const handleTryPlanner = () => {
    navigate(AppRoutesPaths.PHISHING_SIMULATIONS_ROUTE);
  };

  return (
    <StyledCard translate="no">
      <StyledHeader
        onClick={isPending ? undefined : handleToggle}
        type="button"
        style={isPending ? { cursor: 'default' } : undefined}
      >
        <StyledTitleGroup>
          <StyledTitle>
            {isPending
              ? t('account.osintTransparency.title.pending')
              : t('account.osintTransparency.title')}
          </StyledTitle>
          <AppIconTooltip
            target="osint-transparency-tooltip"
            content={t('account.osintTransparency.tooltip')}
          />
          <AppChip
            label={t(chipProps.labelKey)}
            type={chipProps.type}
            size="xs-auto"
            pulsate={isPending}
          />
        </StyledTitleGroup>
        {!isPending && (
          <StyledChevron $isOpen={isOpen} className="pi pi-chevron-down" />
        )}
      </StyledHeader>

      {!isPending && (
        <StyledDescription>
          {t('account.osintTransparency.description.active')}
        </StyledDescription>
      )}

      {isPending && (
        <>
          <StyledDescription>
            {t('account.osintTransparency.description.pending')}
          </StyledDescription>
          <StyledHelperText>
            {t('account.osintTransparency.description.pendingRetry')}
          </StyledHelperText>
        </>
      )}

      {isOpen && (
        <StyledContent>
          {data.state === 'disabled' && (
            <DisabledContent onTurnOn={handleTurnOn} />
          )}

          {data.summary && <ReportContent data={data} />}

          {data.summary && (
            <CtaBanner
              state={data.state}
              onBookDemo={handleBookDemo}
              onTryPlanner={handleTryPlanner}
            />
          )}
        </StyledContent>
      )}
    </StyledCard>
  );
};

/* ── Disabled content ── */

const DisabledContent: React.FC<{ onTurnOn: () => void }> = ({ onTurnOn }) => {
  const { t } = useTranslation();

  return (
    <StyledDisabledPanel>
      <StyledMagnifyingGlass>
        <i className="pi pi-search" />
      </StyledMagnifyingGlass>
      <StyledDisabledHeading>
        {t('account.osintTransparency.disabled.heading')}
      </StyledDisabledHeading>
      <StyledDisabledDescription>
        {t('account.osintTransparency.disabled.description')}
      </StyledDisabledDescription>
      <StyledDisabledButton
        label={t('account.osintTransparency.disabled.cta')}
        onClick={onTurnOn}
      />
    </StyledDisabledPanel>
  );
};

/* ── Report content (beige panel) ── */

const ReportContent: React.FC<{ data: OsintTransparencyResponse }> = ({
  data,
}) => {
  const { t } = useTranslation();

  if (!data.summary) return null;

  return (
    <StyledReportPanel>
      {data.summary.mainRisk && (
        <>
          <StyledSectionTitle>
            {t('account.osintTransparency.mainRisk')}
          </StyledSectionTitle>
          <StyledSectionText>{data.summary.mainRisk}</StyledSectionText>
        </>
      )}

      {data.summary.sections?.map((section, index) => (
        <React.Fragment key={index}>
          <StyledSectionDivider />
          <StyledSectionTitle>{section.title}</StyledSectionTitle>
          <StyledItemList>
            {section.items?.map((item, itemIndex) => (
              <li key={itemIndex}>
                <StyledItemLabel>{item.label}: </StyledItemLabel>
                {item.description}
              </li>
            ))}
          </StyledItemList>
        </React.Fragment>
      ))}

      {data.lastUpdatedAt && (
        <StyledTimestamp>
          {t('account.osintTransparency.timestamp', {
            date: new Date(data.lastUpdatedAt).toLocaleDateString(),
          })}
        </StyledTimestamp>
      )}
    </StyledReportPanel>
  );
};

/* ── CTA banner (dark teal) ── */

const CtaBanner: React.FC<{
  state: OsintTransparencyState;
  onBookDemo: () => void;
  onTryPlanner: () => void;
}> = ({ state, onBookDemo, onTryPlanner }) => {
  const { t } = useTranslation();

  if (state === 'success') return null;

  const config: Record<
    string,
    { heading: string; description: string; cta: string; onClick: () => void }
  > = {
    'free-trial-upsell': {
      heading: t('account.osintTransparency.freeTrialUpsell.heading'),
      description: t('account.osintTransparency.freeTrialUpsell.description'),
      cta: t('account.osintTransparency.freeTrialUpsell.cta'),
      onClick: onBookDemo,
    },
    'paying-upsell': {
      heading: t('account.osintTransparency.payingUpsell.heading'),
      description: t('account.osintTransparency.payingUpsell.description'),
      cta: t('account.osintTransparency.payingUpsell.cta'),
      onClick: onBookDemo,
    },
    'try-planner': {
      heading: t('account.osintTransparency.tryPlanner.heading'),
      description: t('account.osintTransparency.tryPlanner.description'),
      cta: t('account.osintTransparency.tryPlanner.cta'),
      onClick: onTryPlanner,
    },
  };

  const bannerConfig = config[state];
  if (!bannerConfig) return null;

  return (
    <StyledBanner>
      <StyledBannerIconBadge>
        <FishingHookIcon />
      </StyledBannerIconBadge>
      <StyledBannerTextContainer>
        <StyledBannerHeading>{bannerConfig.heading}</StyledBannerHeading>
        <StyledBannerText>{bannerConfig.description}</StyledBannerText>
      </StyledBannerTextContainer>
      <AppButton
        label={bannerConfig.cta}
        onClick={bannerConfig.onClick}
        style={{ marginTop: 8 }}
      />
    </StyledBanner>
  );
};
